import { useEffect, useState } from "react";
import axios from "axios";
import { Star, Quote } from "lucide-react";
import { EXTERNAL_TESTIMONIALS } from "@/lib/site";
import { API } from "@/lib/api";

const SOURCE_BADGE_COLOR = {
    Nextdoor: "bg-emerald-600/20 text-emerald-300 border-emerald-600/40",
    Google: "bg-blue-600/20 text-blue-300 border-blue-600/40",
    Instagram: "bg-pink-600/20 text-pink-300 border-pink-600/40",
    Facebook: "bg-blue-700/20 text-blue-300 border-blue-700/40",
    "Customer Feedback": "bg-red-600/20 text-red-300 border-red-600/40",
};

function StarRow({ rating }) {
    return (
        <span className="inline-flex items-center gap-0.5 text-amber-400">
            {[1, 2, 3, 4, 5].map((n) => (
                <Star
                    key={n}
                    className={`w-3.5 h-3.5 ${
                        n <= rating ? "fill-amber-400" : "text-zinc-700"
                    }`}
                />
            ))}
        </span>
    );
}

export default function TestimonialsSection() {
    const [feedbackTestis, setFeedbackTestis] = useState([]);

    useEffect(() => {
        let mounted = true;
        axios
            .get(`${API}/testimonials`)
            .then((res) => {
                if (mounted) setFeedbackTestis(res.data?.testimonials || []);
            })
            .catch(() => {});
        return () => {
            mounted = false;
        };
    }, []);

    // Combine external + customer feedback. Show up to 6.
    const all = [...EXTERNAL_TESTIMONIALS, ...feedbackTestis].slice(0, 6);

    if (all.length === 0) return null;

    return (
        <section
            className="bg-black border-b border-white/10"
            data-testid="testimonials-section"
        >
            <div className="lbp-container py-16 sm:py-24">
                <div className="flex items-end justify-between flex-wrap gap-4">
                    <div>
                        <p className="text-xs uppercase tracking-widest text-red-500 font-semibold">
                            Word on the street
                        </p>
                        <h2 className="mt-2 font-display font-black uppercase tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl">
                            Testimonials
                        </h2>
                        <p className="mt-3 text-zinc-400 max-w-xl">
                            Real reviews from real customers — pulled in from Nextdoor, customer feedback, and around Hampton Roads.
                        </p>
                    </div>
                </div>

                <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {all.map((t, i) => (
                        <figure
                            key={t.id || i}
                            data-testid={`testimonial-card-${i}`}
                            className="relative rounded-sm border border-white/10 bg-zinc-900 p-6 sm:p-7 flex flex-col"
                        >
                            <Quote className="absolute top-5 right-5 w-6 h-6 text-red-600/40" />
                            <div className="flex items-center gap-2 flex-wrap">
                                {t.rating ? <StarRow rating={t.rating} /> : null}
                                {t.source ? (
                                    <span
                                        className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-widest border ${
                                            SOURCE_BADGE_COLOR[t.source] ||
                                            "bg-zinc-800 text-zinc-300 border-white/10"
                                        }`}
                                    >
                                        {t.source}
                                    </span>
                                ) : null}
                            </div>
                            <blockquote className="mt-4 text-zinc-200 leading-relaxed text-sm sm:text-base flex-1">
                                "{t.text}"
                            </blockquote>
                            <figcaption className="mt-5 pt-4 border-t border-white/5">
                                <div className="font-display font-bold text-white">
                                    {t.author}
                                </div>
                                {t.location ? (
                                    <div className="text-xs text-zinc-500 mt-0.5">
                                        {t.location}
                                    </div>
                                ) : null}
                            </figcaption>
                        </figure>
                    ))}
                </div>
            </div>
        </section>
    );
}
